import { sortBy, uniq, uniqBy } from 'lodash';
import { v4 as uuid } from 'uuid';

import type {
  BrandingCustomFont,
  FontFamily,
  FontStyleOverride,
  FontVariant,
  ShowcaseBranding,
} from '@src/types.ts/story';

/**
 * Strip the "Italic" qualifier so a roman weight and its italic share a key.
 * "Medium Condensed Italic" → "Medium Condensed"; "Bold Italic" → "Bold".
 */
const baseStyleName = (label: string): string =>
  label
    .replace(/\bitalic\b/gi, '')
    .replace(/\s+/g, ' ')
    .trim();

/**
 * Order a family's variants so the picker reads logically: weights run
 * light → heavy, same-weight width variants stay grouped, and each weight
 * sits **directly before its italic twin** ("Medium", then "Medium Italic").
 *
 * Sort key: (groupWeight, baseStyleName, normal-before-italic). `groupWeight`
 * is shared across a base style's roman + italic members (taken from the roman
 * member) so a font-metadata weight mismatch can never split a weight from its
 * italic. Replaces the old "all normal, then all italic" block order.
 */
export const sortFontVariants = (variants: FontVariant[]): FontVariant[] => {
  const groupWeight = new Map<string, number>();
  for (const v of variants) {
    const key = baseStyleName(v.label);
    if (!groupWeight.has(key) || v.style === 'normal') {
      groupWeight.set(key, v.weight);
    }
  }
  return [...variants].sort((a, b) => {
    const ka = baseStyleName(a.label);
    const kb = baseStyleName(b.label);
    const wa = groupWeight.get(ka) ?? a.weight;
    const wb = groupWeight.get(kb) ?? b.weight;
    if (wa !== wb) return wa - wb;
    if (ka !== kb) return ka.localeCompare(kb);
    return (a.style === 'italic' ? 1 : 0) - (b.style === 'italic' ? 1 : 0);
  });
};

/**
 * `branding.fontFamilies` is a DatoCMS JSON field, so GraphQL reads return an
 * already-parsed array. This helper is kept for two reasons: it **normalises
 * variant order** via `sortFontVariants` for every consumer (load-bearing for
 * the pickers), and it **defensively tolerates a raw string** — guarding any
 * legacy/not-yet-migrated record or MobX-store intermediate state. The string
 * branch is a guard, not the primary path.
 */
export const parseFontFamilies = (raw: unknown): FontFamily[] => {
  let families: FontFamily[] = [];
  if (Array.isArray(raw)) {
    families = raw as FontFamily[];
  } else if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) families = parsed as FontFamily[];
    } catch {
      families = [];
    }
  }
  // Normalise variant order for every consumer (editor picker, Brand Kit
  // card) regardless of the order persisted in DatoCMS.
  return families.map(f =>
    f && Array.isArray(f.variants)
      ? { ...f, variants: sortFontVariants(f.variants) }
      : f,
  );
};

/**
 * Mirrors `parseFontFamilies` for the PLAT-9973 Font Style overrides.
 * `branding.fontStyles` is a DatoCMS JSON field (GraphQL returns a parsed
 * array); the string branch is a defensive guard for legacy/not-yet-migrated
 * records, not the primary path.
 */
export const parseFontStyles = (raw: unknown): FontStyleOverride[] => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw as FontStyleOverride[];
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? (parsed as FontStyleOverride[]) : [];
    } catch {
      return [];
    }
  }
  return [];
};

export enum Bold {
  thin = 'Thin',
  extraLight = 'Extra-Light',
  light = 'Light',
  normal = 'Normal',
  medium = 'Medium',
  semiBold = 'Semi-Bold',
  bold = 'Bold',
  extraBold = 'Extra-Bold',
  black = 'Black',
}

export const BoldValues = {
  [Bold.thin]: 100,
  [Bold.extraLight]: 200,
  [Bold.light]: 300,
  [Bold.normal]: 400,
  [Bold.medium]: 500,
  [Bold.semiBold]: 600,
  [Bold.bold]: 700,
  [Bold.extraBold]: 800,
  [Bold.black]: 900,
};

export const DEFAULT_BOLD_VALUES: BoldValue[] = [
  { fontSubfamily: Bold.normal, fontWeight: BoldValues.Normal },
  { fontSubfamily: Bold.semiBold, fontWeight: BoldValues['Semi-Bold'] },
  { fontSubfamily: Bold.bold, fontWeight: BoldValues.Bold },
  { fontSubfamily: Bold.extraBold, fontWeight: BoldValues['Extra-Bold'] },
];

type BoldValue = {
  fontSubfamily: string;
  fontWeight: number;
  fontScriptName?: string;
};

export const getCustomFontFromPostScriptName = (
  fontPostScriptName?: string,
  fonts?: BrandingCustomFont[],
) => {
  if (!fontPostScriptName || !fonts) return undefined;
  const customFonts = uniqBy(fonts, 'postScriptName');
  return customFonts.find(f => f.postScriptName === fontPostScriptName);
};

export const getBoldValuesFromCustomFonts = (
  fontScriptName: string,
  fonts: BrandingCustomFont[],
) => {
  const fontFamily = fonts.find(
    f => f.postScriptName === fontScriptName,
  )?.fontFamily;
  const _fonts = sortBy(
    uniqBy(
      fonts.filter(f => f.fontFamily === fontFamily),
      'fontSubfamily',
    ),
    f => f.usWeightClass,
  );
  const { weightsByValue, fontsByWeight } = _fonts.reduce(
    (acc, font) => ({
      weightsByValue: {
        ...acc.weightsByValue,
        [font.fontSubfamily]: font.usWeightClass,
      },
      fontsByWeight: {
        ...acc.fontsByWeight,
        [font.usWeightClass]: font,
      },
    }),
    {} as {
      weightsByValue: Record<string, number>;
      fontsByWeight: Record<string, BrandingCustomFont>;
    },
  );
  return {
    boldValues: uniq(
      _fonts.map(f => ({
        fontScriptName: f.postScriptName,
        fontSubfamily: f.fontSubfamily,
        fontWeight: f.usWeightClass,
      })),
    ) as BoldValue[],
    boldWeightsByValue: weightsByValue,
    fontsByWeight: fontsByWeight,
  };
};

export const groupCustomFontsIntoFamilies = (
  fonts: BrandingCustomFont[],
): FontFamily[] => {
  const byFamily = new Map<string, BrandingCustomFont[]>();
  for (const font of fonts) {
    const existing = byFamily.get(font.fontFamily) ?? [];
    byFamily.set(font.fontFamily, [...existing, font]);
  }

  return Array.from(byFamily.entries()).map(([name, members]) => {
    const mapped: FontVariant[] = members.map(f => ({
      artifactId: f.artifactId,
      postScriptName: f.postScriptName,
      source: f.source,
      format: f.format,
      archivedAt: f.archivedAt,
      weight: f.usWeightClass,
      style: f.style === 'italic' ? 'italic' : 'normal',
      label: f.fontSubfamily,
      // PLAT-11412 — absent on fonts uploaded before coverage detection.
      ...(f.scriptCoverage ? { scriptCoverage: f.scriptCoverage } : {}),
    }));

    return {
      id: uuid(),
      name,
      originalName: name,
      variants: sortFontVariants(mapped),
    };
  });
};

export const resolveFontVariant = (
  family: FontFamily,
  weight: number,
  style: 'normal' | 'italic',
): FontVariant | undefined => {
  const candidates = family.variants.filter(
    v => v.style === style && !v.archivedAt,
  );
  if (!candidates.length) return undefined;

  const exact = candidates.find(v => v.weight === weight);
  if (exact) return exact;

  return candidates.reduce((closest, v) =>
    Math.abs(v.weight - weight) < Math.abs(closest.weight - weight)
      ? v
      : closest,
  );
};

export const findClosestBoldValueFont = (
  fontsByWeight: Record<string, BrandingCustomFont>,
  target = 400,
) => {
  const closestWeight = Object.keys(fontsByWeight)
    .map(Number)
    .reduce(
      (closest, num) =>
        Math.abs(num - target) < Math.abs(closest - target) ? num : closest,
      Number.MAX_SAFE_INTEGER,
    );
  return { weight: closestWeight, font: fontsByWeight[closestWeight] };
};

/**
 * Returns the subset of DEFAULT_BOLD_VALUES that the document *actually*
 * has loaded for the given font family. This ensures that families like
 * Caveat (which only ships 400–700) show Extra-Bold in the picker.
 *
 * Walking `document.fonts` directly and parsing each loaded face's
 * declared weight (including ranges like "400 700" for variable fonts)
 * is the reliable read.
 */
export function getPopularFontWeights(
  name: string,
): typeof DEFAULT_BOLD_VALUES {
  const loaded = new Set<number>();
  const target = name.toLowerCase().replace(/['"]/g, '').trim();

  document.fonts.forEach(face => {
    const faceFamily = face.family.toLowerCase().replace(/['"]/g, '').trim();
    if (faceFamily !== target) return;

    // FontFace.weight is a string — "400" | "bold" | "400 700" (range).
    const raw = String(face.weight).trim();
    if (raw === 'normal') {
      loaded.add(400);
      return;
    }
    if (raw === 'bold') {
      loaded.add(700);
      return;
    }
    const nums = raw
      .split(/\s+/)
      .map(Number)
      .filter(n => Number.isFinite(n));
    if (nums.length === 2) {
      // Variable-font weight range (e.g. "400 700"). Include every
      // candidate weight that falls inside it.
      const [min, max] = nums;
      for (const bv of DEFAULT_BOLD_VALUES) {
        if (bv.fontWeight >= min && bv.fontWeight <= max) {
          loaded.add(bv.fontWeight);
        }
      }
    } else if (nums.length === 1) {
      loaded.add(nums[0]);
    }
  });

  const available = DEFAULT_BOLD_VALUES.filter(bv => loaded.has(bv.fontWeight));
  if (available.length > 0) return available;
  // Font not loaded in this document — show the two universally safe weights
  return DEFAULT_BOLD_VALUES.filter(
    bv => bv.fontWeight === 400 || bv.fontWeight === 700,
  );
}

/**
 * Resolve the brand-kit font families that should be *visible* in editor
 * surfaces: the structured `fontFamilies` model with archived variants (and
 * fully-archived families) stripped, falling back to grouping the legacy flat
 * `customFonts[]` when no structured families are stored yet.
 *
 * Previously duplicated verbatim across BrandKitFonts,
 * FontFamilyVariantDropdown, TextFontPicker and TextFloatingToolbar; centralised
 * here so every surface agrees on the visible set. See `useBrandFontFamilies`
 * (src/hooks) for the store-reading hook wrapper.
 */
export const resolveBrandFontFamilies = (
  branding:
    | Pick<ShowcaseBranding, 'fontFamilies' | 'customFonts'>
    | null
    | undefined,
): FontFamily[] => {
  const stored = parseFontFamilies(branding?.fontFamilies);
  const activeStored = stored
    .map(f => ({ ...f, variants: f.variants.filter(v => !v.archivedAt) }))
    .filter(f => f.variants.length > 0);
  if (activeStored.length) return activeStored;
  if (branding?.customFonts?.length) {
    return groupCustomFontsIntoFamilies(
      branding.customFonts.filter(f => !f.archivedAt),
    );
  }
  return [];
};

/**
 * Soft-archive a single variant (by `artifactId`) across a family list,
 * preserving every other variant — *including ones already archived*. Callers
 * MUST pass the complete parsed `fontFamilies` (e.g. from `parseFontFamilies`),
 * never the active-filtered view (`resolveBrandFontFamilies`), or
 * previously-archived variants are silently dropped from the persisted JSON.
 */
export const archiveVariantInFamilies = (
  families: FontFamily[],
  artifactId: string,
  archivedAt: string,
): FontFamily[] =>
  families.map(f => ({
    ...f,
    variants: f.variants.map(v =>
      v.artifactId === artifactId ? { ...v, archivedAt } : v,
    ),
  }));

/**
 * Soft-archive every variant of a family (by `id`) across a family list. Like
 * {@link archiveVariantInFamilies}, requires the complete parsed list so prior
 * soft-deletes survive the write.
 */
export const archiveFamilyInFamilies = (
  families: FontFamily[],
  familyId: string,
  archivedAt: string,
): FontFamily[] =>
  families.map(f =>
    f.id === familyId
      ? { ...f, variants: f.variants.map(v => ({ ...v, archivedAt })) }
      : f,
  );

/** Rename a family (by `id`) across a family list, leaving variants untouched. */
export const renameFamilyInFamilies = (
  families: FontFamily[],
  familyId: string,
  newName: string,
): FontFamily[] =>
  families.map(f => (f.id === familyId ? { ...f, name: newName } : f));

/**
 * Whether a popular-font weight row should render as the active selection.
 * Popular fonts carry no per-variant postScriptName, so selection keys on
 * (family name applied to the element) + (row weight === applied weight).
 * Shared by TextFontPicker and FontFamilyVariantDropdown so both highlight the
 * same row — the dropdown previously hard-coded weight `400`, which never
 * matched a non-400 applied weight.
 */
export const isPopularFontVariantSelected = (
  fontName: string,
  currentFontName: string | null,
  rowWeight: number,
  currentFontWeight: string | undefined,
): boolean =>
  fontName === currentFontName &&
  currentFontWeight != null &&
  rowWeight.toString() === currentFontWeight;
